<script setup lang="ts">
import { api } from '@/api';
import { AppPermissionsEnum, type RtSystemQuantityScopeDto, type TextDto } from '@/api/generator';
import PtwAddButton from '@/components/buttons/PtwAddButton.vue';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { EntityInfo } from '@/components/confirmationModals/types';
import { TAB_PREFIX } from '@/components/confirmationModals/useFormConfirmationModal';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import { ContextMenuAction } from '@/components/ContextMenu/types';
import {
  getDefaultContextMenuActions,
  globalContextMenuHeaderItemDefinition,
} from '@/components/ContextMenu/utils';
import EntityChange from '@/components/EntityHistory/EntityChange.vue';
import CommentCount from '@/components/forms/CommentCount/CommentCount.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import OverviewTableHeader from '@/components/OverviewTable/OverviewTableHeader.vue';
import type { SortItem } from '@/components/OverviewTable/types';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { alphabeticalStringCompare, localizedEnumCompare } from '@/utils/compareAndSort';
import { translateEntityProperty, truthy } from '@/utils/helper';
import { buildOdataQuery } from '@/utils/odata';
import { getTranslationPrefix } from '@/utils/translationPrefix';
import type { I18nKey } from '@/utils/typings';
import { useLoading } from '@/utils/useLoading';
import { useTranslation } from 'i18next-vue';
import { ref, watchEffect, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';
import TextOverviewFilter from './components/TextOverviewFilter.vue';
import type { TextOverviewFilterObject, TextScopeFilterObject } from './types';
import { getEntityChangeEvent } from '@/components/EntityHistory/entityChange';
import { useRemainingViewportHeight } from '@/utils/useRemainingVueport';
import { usePermissionsStore } from '@/stores/permissionsStore.ts';
import { useTableScrollElement } from '@/utils/vuetifyMenuHelper';
import { getScopeType, useDeleteTextWithConfirmation } from './utils';

const { t } = useTranslation();
const { hasPermissions } = usePermissionsStore();
const tableRef = useTemplateRef<HTMLDivElement>('table');
const { height } = useRemainingViewportHeight(tableRef);
const tableScrollElement = useTableScrollElement(tableRef);

const headers = [
  {
    title: ``,
    key: 'name',
    sortRaw(textA: TextDto, textB: TextDto) {
      const translatedTextNameA = translateEntityProperty('name', textA);
      const translatedTextNameB = translateEntityProperty('name', textB);
      return translatedTextNameA && translatedTextNameB
        ? alphabeticalStringCompare(translatedTextNameA, translatedTextNameB)
        : -1;
    },
  },
  {
    title: t('UseInSetup_other'),
    key: 'useInSetups',
    sortable: false,
  },
  {
    title: t('StateOfUse'),
    key: 'state',
    sort: localizedEnumCompare('StateOfUseEnum'),
  },
  {
    title: t('Added'),
    key: 'creationTime',
  },
  {
    title: t('LastModification'),
    key: 'lastModificationTime',
  },
  globalContextMenuHeaderItemDefinition,
] as const;

const filterObject = ref<TextOverviewFilterObject>({});

const initialData = ref<TextDto[]>();

const texts = ref<TextDto[]>();

const router = useRouter();

const sortBy: SortItem[] = [{ key: 'name', order: 'asc' }];

function getScopeFilter(filter: TextScopeFilterObject[]) {
  return {
    or: [
      ...filter.map((entry) => ({
        setupScope: {
          any: { [entry.property]: entry.option },
        },
      })),
    ],
  };
}

const {
  action: loadTexts,
  loading,
  loadingFirstTime,
} = useLoading(async () => {
  const query = buildOdataQuery({
    search: filterObject.value?.search || undefined,
    filter: {
      and: [filterObject.value.setupScope].filter(truthy).map(getScopeFilter),
    },
  });

  const { result } = await api.textsApi.getTextList(query);

  if (result?.value) {
    texts.value = result.value;

    // The unfiltered list also provides the filter options, so no extra request is needed.
    // While a filter is active we keep the options from the last unfiltered load.
    if (!filterObject.value.search && !filterObject.value.setupScope?.length) {
      initialData.value = result.value;
    }
  }
}, true);

function navigateTo(id: string, hash?: string) {
  router.push({
    name: routenames.texts.details,
    hash,
    params: { id },
  });
}

const { action: deleteAction } = useDeleteTextWithConfirmation();

function getContextMenuActions(item: TextDto): ContextMenuAction[] {
  const defaultContextMenuActions = getDefaultContextMenuActions(item);
  return [
    {
      ...defaultContextMenuActions.view,
      to: {
        name: routenames.texts.details,
        params: { id: item.id },
      },
    },
    {
      ...defaultContextMenuActions.edit,
      to: {
        name: routenames.texts.edit,
        params: { id: item.id },
      },
    },
    {
      ...defaultContextMenuActions.delete,
      callback: async () => {
        const { result } = await deleteAction({
          param: item.id,
          confirmationOptions: getDefaultDeleteConfirmationOptions(
            {
              ...(item as EntityInfo),
              typeName: 'Text_one',
            },
            t,
          ),
        });

        if (result?.success) {
          loadTexts();
        }
      },
    },
  ];
}

function listUsage(scopes: RtSystemQuantityScopeDto[] | null | undefined) {
  return (scopes ?? [])
    .map((scope) => {
      const scopeType = getScopeType(scope);
      const property = scope.rtSystemSubCategory ? 'rtSystemSubCategory' : 'rtSystemCategory';
      return t(`${getTranslationPrefix(scopeType, property)}.${scope[property]}` as I18nKey);
    })
    .filter(truthy)
    .join(', ');
}

watchEffect(loadTexts);
</script>

<template>
  <BasePage :loading="loadingFirstTime">
    <template #title>
      <BasePageTitle :title="$t('Text_other')" />
    </template>

    <template #actions>
      <PtwAddButton
        v-if="hasPermissions([AppPermissionsEnum.GlobalTextsAdd])"
        :to="{ name: routenames.texts.add }"
      />
    </template>

    <TextOverviewFilter v-model="filterObject" :initialData="initialData" />
    <OverviewTable
      ref="table"
      class="mt-2"
      :headers="headers"
      :items="texts"
      itemLabel="Text"
      data-testid="DataTable_Text"
      :loading="loading"
      :sortBy="sortBy"
      :height="height"
      fixedHeader
      @click:row="
        (_event, data) => {
          navigateTo(data.item.id);
        }
      "
    >
      <template #header.name="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ $t('Name') }}
          <span class="font-weight-medium">| {{ $t('ID') }}</span>
        </OverviewTableHeader>
      </template>

      <template #header.useInSetups="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ $t('UseInSetup_other') }} <br />
          <span class="font-weight-medium">
            {{ $t('SetupCategory_other') }}
          </span>
        </OverviewTableHeader>
      </template>

      <template #item.name="{ item }">
        <IdentifierLabel :identifier="item.iuid" :label="translateEntityProperty('name', item)" />
        <CommentCount :value="item" @click.stop="navigateTo(item.id, `#${TAB_PREFIX}comments`)" />
      </template>

      <template #item.useInSetups="{ item }">
        {{ listUsage(item.setupScope) || EMPTY_FIELD_SIGN }}
      </template>

      <template #item.state="{ item }">
        {{ $t(`StateOfUseEnum.${item.state}` as I18nKey) }}
        <EntityChange
          :entityChange="getEntityChangeEvent(item, 'editing')"
          :showEmptyFieldSign="false"
        />
      </template>

      <template #item.creationTime="{ item }">
        <EntityChange :entityChange="getEntityChangeEvent(item, 'created')" />
      </template>

      <template #item.lastModificationTime="{ item }">
        <EntityChange :entityChange="getEntityChangeEvent(item, 'modified')" />
      </template>

      <template #item.contextMenu="{ item }">
        <ContextMenu
          :actions="getContextMenuActions(item)"
          :closeOnScrollElements="tableScrollElement"
        />
      </template>
    </OverviewTable>
  </BasePage>
</template>
